"use client"
import styled from "@emotion/styled"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Slider, Typography } from "@mui/material"
import { useEffect, useLayoutEffect, useRef, useState } from "react"

/*
 * The value labels float above the track, so the section keeps the 30px gap
 * that reserves room for them under the "תקציב" heading.
 */
const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`

/*
 * MUI centres each thumb on its track position, so the thumbs at either end hang half
 * their width past the track and out of the filter column (END-286). Inset the track
 * by that half-width so the thumbs stop at the column edge instead of over it. The
 * value labels are clamped to this same box by useContainedValueLabels below, so
 * narrowing the track pulls them in with it.
 */
const THUMB_SIZE = 14
const HALF_THUMB = THUMB_SIZE / 2

const StyledSlider = styled(Slider)`
  width: calc(100% - ${THUMB_SIZE}px);
  margin-inline: ${HALF_THUMB}px;

  .MuiSlider-thumb {
    background-color: ${sharedColors.darkGreen};
    height: ${THUMB_SIZE}px;
    width: ${THUMB_SIZE}px;
  }
  .MuiSlider-thumb.Mui-focusVisible {
    border: 1px dashed white;
    outline: 2px solid black;
    outline-offset: 2px;
    box-shadow: none;
  }
  .MuiSlider-track {
    background-color: ${sharedColors.darkGreen};
  }
  .MuiSlider-rail {
    background-color: #ece9e9;
  }
  /*
   * MUI centres the value label on its thumb, so near either end of the track it
   * hangs past the narrow filter column and lands on the result cards (END-270).
   * useContainedValueLabels measures the overhang and writes the correction to
   * --value-label-shift on each thumb. It is applied through the translate
   * property rather than transform, because MUI already owns this element's
   * transform for the vertical offset and the two compose instead of clashing.
   */
  .MuiSlider-valueLabel {
    white-space: nowrap;
    translate: var(--value-label-shift, 0px);
  }
`

const StyledSliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`

/*
 * Nudges each value label back inside the slider's own box when centring it on
 * the thumb would push it out (END-270).
 *
 * Works off measured rectangles rather than the thumb percentage, so it needs no
 * knowledge of the label's font or width and behaves the same in RTL and LTR.
 * The label's width does not change when it is translated, so re-measuring after
 * a correction yields the same answer — there is no feedback loop.
 */
function useContainedValueLabels(
  rootRef: React.RefObject<HTMLDivElement>,
  deps: number[],
) {
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return

    const clamp = () => {
      const slider = root.querySelector(".MuiSlider-root")
      if (!slider) return
      const bounds = slider.getBoundingClientRect()

      slider.querySelectorAll<HTMLElement>(".MuiSlider-thumb").forEach((thumb) => {
        const label = thumb.querySelector<HTMLElement>(".MuiSlider-valueLabel")
        if (!label) return

        const thumbBox = thumb.getBoundingClientRect()
        const center = thumbBox.left + thumbBox.width / 2
        const half = label.getBoundingClientRect().width / 2

        let shift = 0
        if (center - half < bounds.left) shift = bounds.left - (center - half)
        else if (center + half > bounds.right)
          shift = bounds.right - (center + half)

        // Left unrounded: rounding the correction can leave the label a
        // fraction of a pixel outside the box it was just pulled into.
        thumb.style.setProperty("--value-label-shift", `${shift}px`)
      })
    }

    clamp()

    const observer = new ResizeObserver(clamp)
    observer.observe(root)
    // The labels are observed too, not just the column. A cold load measures
    // them in the fallback font, and the webfont swap that follows resizes the
    // label without resizing anything around it — without this the bubble stays
    // under-corrected until the next drag or window resize.
    root
      .querySelectorAll<HTMLElement>(".MuiSlider-valueLabel")
      .forEach((label) => observer.observe(label))
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootRef, ...deps])
}

interface BudgetSectionProps {
  priceRange: number[]
  handleSliderChange: (
    _: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => void
  numberFormat: (value: number) => string
  minPrice: number
  maxPrice: number
}

function BudgetSection({
  priceRange,
  handleSliderChange,
  numberFormat,
  minPrice,
  maxPrice,
}: BudgetSectionProps) {
  // Local state for slider to prevent bounce-back during drag
  const [localPriceRange, setLocalPriceRange] = useState(priceRange)
  const sliderContainerRef = useRef<HTMLDivElement>(null)

  // minPrice/maxPrice belong in here as well: a thumb's position is its value
  // relative to those bounds, so the same value sits somewhere else when the
  // category changes the range.
  useContainedValueLabels(sliderContainerRef, [
    ...localPriceRange,
    minPrice,
    maxPrice,
  ])

  // Sync local state with prop changes (e.g., from URL navigation)
  useEffect(() => {
    setLocalPriceRange(priceRange)
  }, [priceRange])

  const handleChange = (_: Event, newValue: number | number[]) => {
    // Update local state immediately for smooth UI
    if (Array.isArray(newValue)) {
      setLocalPriceRange(newValue as number[])
    }
  }

  const handleCommitted = (
    event: Event | React.SyntheticEvent,
    newValue: number | number[],
  ) => {
    // Determine active thumb by comparing which value changed
    if (!Array.isArray(newValue)) return

    const activeThumb = newValue[0] !== priceRange[0] ? 0 : 1
    handleSliderChange(event as Event, newValue, activeThumb)
  }

  return (
    <StyledSection>
      <Typography variant="body2" fontWeight={600} id="budget-label">
        תקציב
      </Typography>
      <StyledSliderContainer ref={sliderContainerRef}>
        <StyledSlider
          aria-labelledby="budget-label"
          value={localPriceRange}
          onChange={handleChange}
          onChangeCommitted={handleCommitted}
          min={minPrice}
          max={maxPrice}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => numberFormat(value as number)}
          getAriaValueText={(value) => numberFormat(value)}
          disableSwap
        />
        <StyledLabelsRow>
          <Typography variant="body2" fontWeight={600}>
            {numberFormat(localPriceRange[0])}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {numberFormat(localPriceRange[1])}
          </Typography>
        </StyledLabelsRow>
      </StyledSliderContainer>
    </StyledSection>
  )
}

export default BudgetSection
